<template>
  <a-drawer
    v-model:visible="visible"
    title="客户账号详情"
    width="1200px"
    :mask-closable="true"
    @cancel="handleCancel"
    ok-text="关闭"
    :hide-cancel="true"
  >
    <div class="detail-drawer-content">
      <!-- 基本信息 -->
      <a-card class="info-card" title="基本信息">
        <a-descriptions :column="2" bordered>
          <a-descriptions-item label="账号ID">{{ accountDetail.id }}</a-descriptions-item>
          <a-descriptions-item label="账号编码">{{ accountDetail.code }}</a-descriptions-item>
          <a-descriptions-item label="用户名">{{ accountDetail.username }}</a-descriptions-item>
<!--          <a-descriptions-item label="登录密码">{{ accountDetail.password || '********' }}</a-descriptions-item>-->
          <a-descriptions-item label="昵称">{{ accountDetail.nickname || '—' }}</a-descriptions-item>
          <a-descriptions-item label="手机号">{{ accountDetail.phone || '—' }}</a-descriptions-item>
          <a-descriptions-item label="邮箱">{{ accountDetail.email || '—' }}</a-descriptions-item>
          <a-descriptions-item label="性别">{{ getGenderText(accountDetail.gender) }}</a-descriptions-item>
          <a-descriptions-item label="账号状态">
            <a-tag :color="getStatusColor(accountDetail.status)">
              {{ getStatusText(accountDetail.status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ formatDate(accountDetail.createTime) }}</a-descriptions-item>
          <a-descriptions-item label="修改时间">{{ formatDate(accountDetail.updateTime) }}</a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 关联客户信息 -->
      <a-card class="info-card" title="关联客户信息">
        <template #extra>
          <a-button type="primary" size="small" @click="showAddCustomerModal">
            <template #icon>
              <icon-plus style="stroke: #fff" />
            </template>
            添加客户
          </a-button>
        </template>
        
        <a-table
          row-key="id"
          :columns="customerColumns"
          :data="customerList"
          :pagination="false"
          :loading="customerLoading"
          size="small"
          :bordered="{cell:true}"
          :scroll="{ x: 1000 }"
        >
          <template #createTime="{ record }">
            {{ formatDate(record.createTime) }}
          </template>
          
          <template #action="{ record }">
            <a-popconfirm
              content="确定要删除该关联客户吗？"
              @ok="deleteRelation(record.relationId)"
              ok-text="确定"
              cancel-text="取消"
            >
              <a-button type="text" size="mini" status="danger">
                <template #icon>
                  <icon-delete />
                </template>
                删除
              </a-button>
            </a-popconfirm>
          </template>
        </a-table>
      </a-card>

      <!-- 关联门店信息 -->
      <a-card class="info-card" title="关联门店信息">
        <template #extra>
          <a-button type="primary" size="small" @click="showAddStoreModal">
            <template #icon>
              <icon-plus  style="stroke: #fff"/>
            </template>
            添加门店
          </a-button>
        </template>
        
        <a-table
          row-key="id"
          :columns="storeColumns"
          :data="customerStoreList"
          :pagination="false"
          :loading="storeLoading"
          size="small"
          :bordered="{cell:true}"
          :scroll="{ x: 1000 }"
        >
          <template #autoSendEmail="{ record }">
            <a-tag :color="record.autoSendEmail ? 'green' : 'gray'">
              {{ record.autoSendEmail ? '是' : '否' }}
            </a-tag>
          </template>
          
          <template #createTime="{ record }">
            {{ formatDate(record.createTime) }}
          </template>
          
          <template #action="{ record }">
            <a-popconfirm
              content="确定要删除该关联门店吗？"
              @ok="deleteRelation(record.relationId)"
              ok-text="确定"
              cancel-text="取消"
            >
              <a-button type="text" size="mini" status="danger">
                <template #icon>
                  <icon-delete />
                </template>
                删除
              </a-button>
            </a-popconfirm>
          </template>
        </a-table>
      </a-card>
    </div>

    <!-- 添加客户关联模态框 -->
    <add-customer-modal
      v-model="isShowAddCustomerModal"
      :account-id="accountDetail.id"
      :customer-id-List="customerIdList"
      @success="handleAddCustomerSuccess"
    />

    <!-- 添加门店关联模态框 -->
    <add-store-modal
      v-model="isShowAddStoreModal"
      :account-id="accountDetail.id"
      :customer-store-id-List="customerStoreIdList"
      @success="handleAddStoreSuccess"
    />
  </a-drawer>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Message } from "@arco-design/web-vue"
import { 
  getCustomerAccountInfo,
  deleteOneRelation,
} from '@/apis/customer-data'
import AddCustomerModal from './AddCustomerModal.vue'
import AddStoreModal from './AddStoreModal.vue'
import {
  IconPlus,
  IconDelete, IconSearch
} from '@arco-design/web-vue/es/icon'
import {timestampNumber2dateString} from "@/utils/date-util";

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  accountId: {
    type: [String, Number],
    default: null
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue'])

// 响应式数据
const visible = ref(false)
const accountDetail = ref({})
const customerList = ref([])
const customerStoreList = ref([])
const customerIdList = ref([])
const customerStoreIdList = ref([])
const customerLoading = ref(false)
const storeLoading = ref(false)
const isShowAddCustomerModal = ref(false)
const isShowAddStoreModal = ref(false)

// 客户信息表格列定义
const customerColumns = ref([
  { title: 'ID', dataIndex: 'id', width: 80 },
  { title: '客户编码', dataIndex: 'customerCode', width: 120 },
  { title: '客户名称', dataIndex: 'customerName', width: 150, ellipsis: true, tooltip: true },
  { title: '联系人', dataIndex: 'contactPerson', width: 100 },
  { title: '联系电话', dataIndex: 'contactPhone', width: 130 },
  { title: '联系邮箱', dataIndex: 'contactEmail', width: 180, ellipsis: true, tooltip: true },
  // { title: '公司抬头ID', dataIndex: 'companyHeaderId', width: 120 },
  // { title: '客户地址', dataIndex: 'address', width: 200, ellipsis: true, tooltip: true },
  // { title: '省份', dataIndex: 'province', width: 80 },
  // { title: '城市', dataIndex: 'city', width: 80 },
  // { title: '区县', dataIndex: 'district', width: 80 },
  // { title: '详细地址', dataIndex: 'detailedAddress', width: 200, ellipsis: true, tooltip: true },
  // { title: '经度', dataIndex: 'longitude', width: 100 },
  // { title: '纬度', dataIndex: 'latitude', width: 100 },
  // { title: '客户状态', dataIndex: 'status', width: 80 },
  // { title: '客户等级', dataIndex: 'level', width: 80 },
  // { title: '客户来源', dataIndex: 'source', width: 100 },
  // { title: '备注', dataIndex: 'remark', width: 150, ellipsis: true, tooltip: true },
  { title: '创建时间', dataIndex: 'createTime', width: 160, slotName: 'createTime' },
  { title: '操作', dataIndex: 'action', width: 80, slotName: 'action', align: 'center', fixed: 'right' }
])

// 门店信息表格列定义
const storeColumns = ref([
  { title: 'ID', dataIndex: 'id', width: 80 },
  { title: '门店编码', dataIndex: 'storeCode', width: 120 },
  { title: '门店名称', dataIndex: 'storeName', width: 150, ellipsis: true, tooltip: true },
  // { title: '所属客户ID', dataIndex: 'customerId', width: 100 },
  // { title: '门店类型ID', dataIndex: 'storeTypeId', width: 100 },
  // { title: '所属部门', dataIndex: 'department', width: 120 },
  { title: '联系人', dataIndex: 'contactPerson', width: 100 },
  { title: '联系电话', dataIndex: 'contactPhone', width: 130 },
  { title: '门店邮箱', dataIndex: 'email', width: 180, ellipsis: true, tooltip: true },
  // { title: '自动发送邮件', dataIndex: 'autoSendEmail', width: 120, slotName: 'autoSendEmail' },
  // { title: '省份', dataIndex: 'province', width: 80 },
  // { title: '城市', dataIndex: 'city', width: 80 },
  // { title: '区县', dataIndex: 'district', width: 80 },
  // { title: '详细地址', dataIndex: 'detailedAddress', width: 200, ellipsis: true, tooltip: true },
  // { title: '经度', dataIndex: 'longitude', width: 100 },
  // { title: '纬度', dataIndex: 'latitude', width: 100 },
  // { title: '营业时间', dataIndex: 'businessHours', width: 120 },
  // { title: '面积(㎡)', dataIndex: 'areaSize', width: 100 },
  // { title: '楼层数', dataIndex: 'floorCount', width: 80 },
  // { title: '门店状态', dataIndex: 'status', width: 80 },
  // { title: '备注', dataIndex: 'remark', width: 150, ellipsis: true, tooltip: true },
  { title: '创建时间', dataIndex: 'createTime', width: 160, slotName: 'createTime' },
  { title: '操作', dataIndex: 'action', width: 80, slotName: 'action', align: 'center', fixed: 'right' }
])

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal && props.accountId) {
    fetchAccountDetail()
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 获取账号详情
const fetchAccountDetail = async () => {
  if (!props.accountId) {
    Message.warning('客户账户ID不能为空')
    return
  }

  storeLoading.value = true
  customerLoading.value = true
  
  try {
    const response = await getCustomerAccountInfo(props.accountId)
    if (response.code === 0) {
      accountDetail.value = response.data || {}
      customerList.value = response.data.customerList || []
      customerStoreList.value = response.data.customerStoreList || []
    } else {
      Message.error(response.message || '获取账号详情失败')
      console.error('获取账号详情失败:', response)
    }
  } catch (error) {
    Message.error('获取账号详情失败')
    console.error('获取账号详情失败:', error)
  } finally {
    storeLoading.value = false
    customerLoading.value = false
  }
}

// 删除客户关联
const deleteRelation = async (relationId) => {
  try {
    const response = await deleteOneRelation(relationId)
    if (response.code === 0) {
      Message.success('删除关联信息成功')
      await fetchAccountDetail()
    } else {
      Message.error(response.message || '删除关联信息失败')
      console.error(response)
    }
  } catch (error) {
    Message.error('删除关联信息失败')
    console.error('删除关联信息失败:', error)
  }
}

// 显示添加客户模态框
const showAddCustomerModal = () => {
  customerIdList.value = customerList.value.map(item => item.id)
  console.log(customerIdList.value)
  isShowAddCustomerModal.value = true
}

// 显示添加门店模态框
const showAddStoreModal = () => {
  customerStoreIdList.value = customerStoreList.value.map(item => item.id)
  isShowAddStoreModal.value = true
}

// 处理添加客户成功
const handleAddCustomerSuccess = () => {
  fetchAccountDetail()
}

// 处理添加门店成功
const handleAddStoreSuccess = () => {
  fetchAccountDetail()
}

// 关闭抽屉
const handleCancel = () => {
  visible.value = false
}

// 格式化方法
// TODO 待替换数据字典
const getGenderText = (gender) => {
  const map = { '3': '未设置', '1': '男', '2': '女' }
  return map[gender] || '未知'
}

const getStatusColor = (status) => {
  const map = { '0': 'green', '1': 'red' }
  return map[status] || 'gray'
}

const getStatusText = (status) => {
  const map = { '0': '正常', '1': '禁用' }
  return map[status] || '未知'
}

const formatDate = (dateString) => {
  if (!dateString) return '—'
  // return dateString.substring(0, 16).replace('T', ' ');
  return timestampNumber2dateString(dateString)
}
</script>

<style scoped>
.detail-drawer-content {
  padding: 16px;
}

.info-card {
  margin-bottom: 20px;
}

.info-card:last-child {
  margin-bottom: 0;
}
</style>